import { supabase } from '@/lib/supabase'

export type StabilizationEpisodeStatus =
  | 'intake' | 'capacity_review' | 'scheduled' | 'assessment'
  | 'service' | 'follow_up' | 'closed'

export type StabilizationOutcome =
  | 'MET' | 'PARTIALLY MET' | 'CONNECTED'
  | 'PENDING' | 'UNABLE TO VERIFY' | 'UNMET'

export interface ResourceDirectoryRecord {
  id: string
  resource_id: string
  organization_name: string
  category: string
  service_description: string
  phone: string | null
  email: string | null
  website: string | null
  address: string | null
  service_area: string | null
  eligibility_summary: string | null
  access_procedure: string | null
  cost: string | null
  hours: string | null
  status: string
  visibility: 'public' | 'internal'
  last_verified: string | null
  verification_source: string | null
  navigator_notes: string | null
}

export interface StabilizationEpisode {
  id: string
  episode_number: string
  household_id: string
  case_id: string | null
  status: StabilizationEpisodeStatus
  primary_goal: string | null
  client_priority: string | null
  safe_contact_method: string | null
  consent_follow_up: boolean
  opened_at: string
  closed_at: string | null
}

export type StabilizationPriority = 'urgent' | 'referral' | 'standard'
export type StabilizationPriorityWindow = '1-7 days' | '8-30 days' | '31-60 days'

export interface StabilizationNeed {
  id: string
  episode_id: string
  need_category: string
  need_detail: string | null
  priority: StabilizationPriority
  priority_window: StabilizationPriorityWindow
  wee_action: string | null
  external_referral: string | null
  status: string
}

export interface StabilizationSupplyProduct {
  id: string
  name: string
  size: string | null
  category_id: string
  product_categories?: { name: string } | { name: string }[] | null
}

export interface StabilizationSupplyRequest {
  id: string
  episode_id: string
  product_id: string
  requested_quantity: number
  approved_quantity: number | null
  distributed_quantity: number | null
  status: 'requested' | 'approved' | 'partially_provided' | 'provided' | 'not_available' | 'cancelled'
  notes: string | null
  created_at: string
  products?: Pick<StabilizationSupplyProduct, 'id' | 'name' | 'size'>
}

export async function listActiveSupplyProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('id,name,size,category_id,product_categories(name)')
    .eq('is_active', true)
    .is('deleted_at', null)
    .order('name')
  if (error) throw error
  return (data ?? []) as StabilizationSupplyProduct[]
}

export async function listEpisodeSupplyRequests(episodeId: string) {
  const { data, error } = await supabase
    .from('stabilization_supply_requests')
    .select('*,products(id,name,size)')
    .eq('episode_id', episodeId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as StabilizationSupplyRequest[]
}

export async function createEpisodeSupplyRequests(
  episodeId: string,
  selections: Record<string, number>,
  userId?: string | null,
) {
  const rows = Object.entries(selections)
    .filter(([, quantity]) => Number.isInteger(quantity) && quantity > 0)
    .map(([product_id, requested_quantity]) => ({
      episode_id: episodeId,
      product_id,
      requested_quantity,
      status: 'requested' as const,
      created_by: userId ?? null,
    }))
  if (!rows.length) return []
  const { data, error } = await supabase
    .from('stabilization_supply_requests')
    .insert(rows)
    .select('id,episode_id,product_id,requested_quantity,status,created_at')
  if (error) throw error
  return data ?? []
}

export async function listPublicResources(category?: string) {
  let query = supabase
    .from('resource_directory')
    .select('*')
    .eq('visibility', 'public')
    .neq('status', 'Closed')
    .order('organization_name')

  if (category) query = query.eq('category', category)
  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as ResourceDirectoryRecord[]
}

export async function searchPublicResources(term: string) {
  const { data, error } = await supabase
    .from('resource_directory')
    .select('*')
    .eq('visibility', 'public')
    .neq('status', 'Closed')
    .or(
      `organization_name.ilike.%${term}%,service_description.ilike.%${term}%,category.ilike.%${term}%`
    )
    .order('organization_name')

  if (error) throw error
  return (data ?? []) as ResourceDirectoryRecord[]
}

export async function getEpisode(id: string) {
  const { data, error } = await supabase
    .from('stabilization_episodes')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as StabilizationEpisode
}

export async function listEpisodesForCase(caseId: string) {
  const { data, error } = await supabase
    .from('stabilization_episodes')
    .select('*')
    .eq('case_id', caseId)
    .is('deleted_at', null)
    .order('opened_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as StabilizationEpisode[]
}

export async function listEpisodesForHousehold(householdId: string) {
  const { data, error } = await supabase
    .from('stabilization_episodes')
    .select('*')
    .eq('household_id', householdId)
    .is('deleted_at', null)
    .order('opened_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as StabilizationEpisode[]
}

export async function createEpisode(input: {
  household_id: string
  case_id?: string | null
  primary_goal?: string | null
  client_priority?: string | null
  safe_contact_method?: string | null
  consent_follow_up?: boolean
  created_by?: string | null
}) {
  const { data, error } = await supabase
    .from('stabilization_episodes')
    .insert({ status: 'intake', ...input })
    .select('*')
    .single()

  if (error) throw error
  return data as StabilizationEpisode
}

export async function updateEpisode(
  id: string,
  updates: Partial<Pick<
    StabilizationEpisode,
    'status' | 'primary_goal' | 'client_priority' |
    'safe_contact_method' | 'consent_follow_up' | 'closed_at'
  >>
) {
  const { data, error } = await supabase
    .from('stabilization_episodes')
    .update(updates)
    .eq('id', id)
    .select('*')
    .single()

  if (error) throw error
  return data as StabilizationEpisode
}

export async function listNeeds(episodeId: string) {
  const { data, error } = await supabase
    .from('stabilization_needs')
    .select('*')
    .eq('episode_id', episodeId)
    .is('deleted_at', null)
    .order('created_at')

  if (error) throw error
  return (data ?? []) as StabilizationNeed[]
}

export async function addNeed(input: {
  episode_id: string
  need_category: string
  need_detail?: string | null
  priority?: StabilizationNeed['priority']
  priority_window?: string
  wee_action?: string | null
  external_referral?: string | null
}) {
  const { data, error } = await supabase
    .from('stabilization_needs')
    .insert(input)
    .select('*')
    .single()

  if (error) throw error
  return data as StabilizationNeed
}

export async function updateNeed(id: string, updates: Partial<Omit<StabilizationNeed, 'id' | 'episode_id'>>) {
  const { data, error } = await supabase
    .from('stabilization_needs')
    .update(updates)
    .eq('id', id)
    .select('*')
    .single()

  if (error) throw error
  return data as StabilizationNeed
}

export async function createResourceConnection(input: {
  episode_id: string
  need_id?: string | null
  resource_id?: string | null
  connection_type: 'navigation' | 'referral' | 'provision' | 'coordination' | 'connection'
  accepted?: boolean | null
  service_received?: 'yes' | 'no' | 'pending' | 'unknown'
  provider_response?: string | null
  notes?: string | null
  created_by?: string | null
}) {
  const { data, error } = await supabase
    .from('resource_connections')
    .insert(input)
    .select('*')
    .single()

  if (error) throw error
  return data
}

export async function recordReceipt(input: {
  connection_id: string
  receipt_status: 'verified' | 'pending' | 'unable_to_verify' | 'not_received'
  evidence_type?: 'client_acknowledgment' | 'provider_confirmation' | 'distribution_record' | 'document' | 'other'
  evidence_reference?: string | null
  receipt_date?: string | null
  verified_by?: string | null
  verification_notes?: string | null
}) {
  const { data, error } = await supabase
    .from('evidence_receipts')
    .insert(input)
    .select('*')
    .single()

  if (error) throw error
  return data
}

export async function scheduleFollowUp(input: {
  episode_id: string
  follow_up_type: '24-72h' | '30d' | '60d' | '90d'
  due_date: string
  created_by?: string | null
}) {
  const { data, error } = await supabase
    .from('stabilization_follow_ups')
    .insert(input)
    .select('*')
    .single()

  if (error) throw error
  return data
}

export async function recordOutcome(input: {
  episode_id: string
  outcome_status: StabilizationOutcome
  outcome_category?: string | null
  outcome_summary?: string | null
  measured_by?: string | null
  evidence_reference?: string | null
}) {
  const { data, error } = await supabase
    .from('stabilization_outcomes')
    .insert(input)
    .select('*')
    .single()

  if (error) throw error
  return data
}


export interface ServiceEpisodeSubmission {
  household_id: string
  case_id?: string | null
  primary_goal?: string | null
  client_priority: StabilizationPriority
  priority_window: StabilizationPriorityWindow
  need_category: string
  need_detail?: string | null
  wee_action?: string | null
  external_referral?: string | null
  resource_id?: string | null
  connection_type?: 'navigation' | 'referral' | 'provision' | 'coordination' | 'connection' | null
  accepted?: boolean | null
  service_received?: 'yes' | 'no' | 'pending' | 'unknown' | null
  provider_response?: string | null
  receipt_status?: 'verified' | 'pending' | 'unable_to_verify' | 'not_received' | null
  evidence_type?: 'client_acknowledgment' | 'provider_confirmation' | 'distribution_record' | 'document' | 'other' | null
  evidence_reference?: string | null
  follow_up_type?: '24-72h' | '30d' | '60d' | '90d' | null
  follow_up_date?: string | null
  outcome_status?: StabilizationOutcome | null
  outcome_category?: string | null
  outcome_summary?: string | null
  created_by?: string | null
}


export type AppendServiceEpisodeActivitySubmission = Omit<ServiceEpisodeSubmission, 'household_id' | 'case_id'> & {
  episode_id: string
  interaction_notes?: string | null
}

/** Add a new service interaction to an existing episode without creating a new episode or case number. */
export async function appendServiceEpisodeActivity(input: AppendServiceEpisodeActivitySubmission) {
  const { data, error } = await supabase.rpc('append_stabilization_service_activity', {
    p_episode_id: input.episode_id,
    p_primary_goal: input.primary_goal ?? null,
    p_client_priority: input.client_priority,
    p_priority_window: input.priority_window,
    p_need_category: input.need_category,
    p_need_detail: input.need_detail ?? null,
    p_wee_action: input.wee_action ?? null,
    p_external_referral: input.external_referral ?? null,
    p_resource_id: input.resource_id ?? null,
    p_connection_type: input.connection_type ?? null,
    p_accepted: input.accepted ?? null,
    p_service_received: input.service_received ?? 'pending',
    p_provider_response: input.provider_response ?? null,
    p_receipt_status: input.receipt_status ?? null,
    p_evidence_type: input.evidence_type ?? null,
    p_evidence_reference: input.evidence_reference ?? null,
    p_follow_up_type: input.follow_up_type ?? null,
    p_follow_up_date: input.follow_up_date ?? null,
    p_outcome_status: input.outcome_status ?? 'PENDING',
    p_outcome_category: input.outcome_category ?? null,
    p_outcome_summary: input.outcome_summary ?? null,
    p_interaction_notes: input.interaction_notes ?? null,
    p_created_by: input.created_by ?? null,
  })
  if (error) throw error
  return data as string
}

export async function createServiceEpisode(input: ServiceEpisodeSubmission) {
  const { data, error } = await supabase.rpc('create_stabilization_service_episode', {
    p_household_id: input.household_id,
    p_case_id: input.case_id ?? null,
    p_primary_goal: input.primary_goal ?? null,
    p_client_priority: input.client_priority,
    p_priority_window: input.priority_window,
    p_need_category: input.need_category,
    p_need_detail: input.need_detail ?? null,
    p_wee_action: input.wee_action ?? null,
    p_external_referral: input.external_referral ?? null,
    p_resource_id: input.resource_id ?? null,
    p_connection_type: input.connection_type ?? null,
    p_accepted: input.accepted ?? null,
    p_service_received: input.service_received ?? 'pending',
    p_provider_response: input.provider_response ?? null,
    p_receipt_status: input.receipt_status ?? null,
    p_evidence_type: input.evidence_type ?? null,
    p_evidence_reference: input.evidence_reference ?? null,
    p_follow_up_type: input.follow_up_type ?? null,
    p_follow_up_date: input.follow_up_date ?? null,
    p_outcome_status: input.outcome_status ?? 'PENDING',
    p_outcome_category: input.outcome_category ?? null,
    p_outcome_summary: input.outcome_summary ?? null,
    p_created_by: input.created_by ?? null,
  })
  if (error) throw error
  if (!data) throw new Error('The service episode was not created.')
  return getEpisode(data as string)
}
